/**
 * Board templates (pure). Each builder returns the full element list with
 * DETERMINISTIC ids (`tpl-<template>-…`), version 1 and id-derived nonces, so
 * two clients that open the same empty board at once seed identical elements
 * and the server's version/nonce rule collapses them to one copy.
 */

import { arrowElement, FONT, frameElement, labelledShape, textElement, type El } from './elements';
import { stickyElements, type StickyColor } from './sticky';

export type TemplateId = 'blank' | 'brainstorm' | 'retro' | 'kanban' | 'flowchart';

export interface TemplateInfo {
  id: TemplateId;
  name: string;
  description: string;
}

export const TEMPLATES: TemplateInfo[] = [
  { id: 'blank', name: 'Blank board', description: 'Start from an empty canvas' },
  { id: 'brainstorm', name: 'Brainstorm', description: 'A frame with a topic and idea stickies' },
  { id: 'retro', name: 'Retrospective', description: 'Went well · To improve · Actions' },
  { id: 'kanban', name: 'Kanban', description: 'To do · Doing · Done' },
  { id: 'flowchart', name: 'Flowchart', description: 'Start → process → decision → end' },
];

export function isTemplateId(v: unknown): v is TemplateId {
  return TEMPLATES.some((t) => t.id === v);
}

const pid = (t: TemplateId, name: string) => `tpl-${t}-${name}`;

function brainstorm(): El[] {
  const t: TemplateId = 'brainstorm';
  const frame = frameElement(pid(t, 'frame'), { name: 'Brainstorm', x: 0, y: 0, width: 1100, height: 760 });
  const f = frame.id;
  const out: El[] = [frame];
  out.push(...stickyElements({ id: pid(t, 'topic'), cx: 550, cy: 380, size: 240, color: 'yellow', text: 'Topic: what should we explore?', frameId: f }));
  const ideas: [number, number, StickyColor, string][] = [
    [190, 150, 'orange', 'Idea'],
    [550, 120, 'green', 'Idea'],
    [910, 150, 'blue', 'Idea'],
    [190, 610, 'pink', 'Idea'],
    [550, 640, 'purple', 'Idea'],
    [910, 610, 'grey', 'Idea'],
  ];
  ideas.forEach(([cx, cy, color, text], i) => out.push(...stickyElements({ id: pid(t, `idea-${i + 1}`), cx, cy, color, text, size: 180, frameId: f })));
  return out;
}

function columns(t: TemplateId, cols: { key: string; name: string; color: StickyColor; samples: string[] }[], title: string): El[] {
  const W = 380;
  const H = 820;
  const GAP = 40;
  const out: El[] = [
    textElement(pid(t, 'title'), { text: title, x: 0, y: -80, fontSize: 36, fontFamily: FONT.Brand, width: 520, height: 45 }),
  ];
  cols.forEach((c, i) => {
    const x = i * (W + GAP);
    const frame = frameElement(pid(t, c.key), { name: c.name, x, y: 0, width: W, height: H });
    out.push(frame);
    c.samples.forEach((s, j) => {
      out.push(...stickyElements({ id: pid(t, `${c.key}-${j + 1}`), cx: x + W / 2, cy: 140 + j * 230, color: c.color, text: s, frameId: frame.id }));
    });
  });
  return out;
}

function retro(): El[] {
  return columns(
    'retro',
    [
      { key: 'went-well', name: 'Went well', color: 'green', samples: ['What went well?'] },
      { key: 'to-improve', name: 'To improve', color: 'pink', samples: ['What could be better?'] },
      { key: 'actions', name: 'Actions', color: 'blue', samples: ['Action item (owner)'] },
    ],
    'Retrospective',
  );
}

function kanban(): El[] {
  return columns(
    'kanban',
    [
      { key: 'todo', name: 'To do', color: 'yellow', samples: ['Task 1', 'Task 2'] },
      { key: 'doing', name: 'Doing', color: 'orange', samples: ['Task 3'] },
      { key: 'done', name: 'Done', color: 'green', samples: ['Task 4'] },
    ],
    'Kanban',
  );
}

function flowchart(): El[] {
  const t: TemplateId = 'flowchart';
  const id = (n: string) => pid(t, n);
  const W = 220;
  const cx = W / 2;
  const nodes = {
    start: { type: 'ellipse' as const, y: 0, h: 80, label: 'Start', bg: '#b2f2bb' },
    process: { type: 'rectangle' as const, y: 160, h: 100, label: 'Process', bg: '#a5d8ff' },
    decision: { type: 'diamond' as const, y: 340, h: 160, label: 'Decision?', bg: '#ffec99' },
    end: { type: 'ellipse' as const, y: 580, h: 80, label: 'End', bg: '#ffc9c9' },
  };
  const arrows: { key: string; from: keyof typeof nodes; to: keyof typeof nodes; points: [number, number][]; label?: string }[] = [
    { key: 'a1', from: 'start', to: 'process', points: [[cx, 88], [cx, 152]] },
    { key: 'a2', from: 'process', to: 'decision', points: [[cx, 268], [cx, 332]] },
    { key: 'a3', from: 'decision', to: 'end', points: [[cx, 508], [cx, 572]], label: 'Yes' },
    { key: 'a4', from: 'decision', to: 'process', points: [[W + 8, 420], [W + 110, 420], [W + 110, 210], [W + 8, 210]], label: 'No' },
  ];
  const bound = new Map<string, { id: string; type: 'arrow' }[]>();
  for (const a of arrows) {
    for (const n of [a.from, a.to]) {
      const list = bound.get(n) ?? [];
      list.push({ id: id(a.key), type: 'arrow' });
      bound.set(n, list);
    }
  }
  const out: El[] = [];
  for (const [key, n] of Object.entries(nodes)) {
    out.push(
      ...labelledShape(n.type, id(key), {
        x: 0,
        y: n.y,
        width: W,
        height: n.h,
        backgroundColor: n.bg,
        fillStyle: 'solid',
        label: n.label,
        boundElements: bound.get(key),
      }),
    );
  }
  for (const a of arrows) {
    out.push(...arrowElement(id(a.key), { points: a.points, start: { id: id(a.from) }, end: { id: id(a.to) }, label: a.label }));
  }
  return out;
}

/** Elements for a template (empty for 'blank'). */
export function buildTemplate(t: TemplateId): El[] {
  switch (t) {
    case 'brainstorm':
      return brainstorm();
    case 'retro':
      return retro();
    case 'kanban':
      return kanban();
    case 'flowchart':
      return flowchart();
    default:
      return [];
  }
}
